import {CheckboxCheckedChangeDetails} from "@chakra-ui/react";
import {typedMemo} from "../../../utils/typescript";
import {LinkProvider} from "../../LinkContext";
import {useCallback, useMemo} from "react";
import {SelectionCheckbox} from "./SelectionCheckbox";
import {ColumnId, ExtractTableContext, ExtractTableItem, ExtractTableItemKey, TableConfig} from "../../../hooks/table";
import {BaseTableCell, BaseTableCellLink} from "./base";
import {cellRowLink, rowInteraction} from "./rowInteraction";
import {TableVerticalAlign} from ".";

/**
 * The active-row tint, painted on every cell because the row element belongs to
 * the table implementation above and cells fill the row.
 */
const ACTIVE_ROW_BG = "colorPalette.subtle/50";

export const RowContent = typedMemo(
  <C extends TableConfig>({
    config,
    item,
    context,
    columnIds,
    verticalAlign,
    selectedRowKeys,
    setRowSelected,
    supportsSelection,
  }: {
    config: C;
    item: ExtractTableItem<C>;
    context: ExtractTableContext<C>;
    columnIds: ColumnId<C>[];
    verticalAlign: TableVerticalAlign;
    selectedRowKeys: Set<ExtractTableItemKey<C>>;
    setRowSelected: (key: ExtractTableItemKey<C>, action: boolean | ((prev: boolean) => boolean)) => void;
    supportsSelection: boolean;
  }) => {
    const key = useMemo(() => config.extractUniqueKey(item) as ExtractTableItemKey<C>, [config, item]);
    const isChecked = useMemo(() => selectedRowKeys.has(key), [key, selectedRowKeys]);

    const isSelectable = config.getRowIsSelectable?.(item, context) ?? true;
    const interaction = rowInteraction({
      link: config.getRowLink?.(item, context),
      picks: config.onRowClick != null,
      disabled: config.getRowIsDisabled?.(item, context) ?? false,
    });
    const isActive = config.getRowIsActive?.(item, context) ?? false;
    const activeProps = isActive ? {bg: ACTIVE_ROW_BG} : {};

    const onRowClick = config.onRowClick;
    const pick = useCallback(() => onRowClick?.(item, context), [onRowClick, item, context]);
    const pickProps =
      interaction.type === "pick"
        ? {onClick: pick, cursor: "pointer"}
        : interaction.type === "refused"
          ? {cursor: "not-allowed"}
          : {};

    const onCheckedChange = useCallback(
      (d: CheckboxCheckedChangeDetails) => {
        const checked = !!d.checked;
        setRowSelected(key, checked);
      },
      [key, setRowSelected],
    );

    const inner = (
      <>
        {supportsSelection && (
          <BaseTableCell
            // Remove any padding - we instead want to size the checkbox label to
            // the full size of the cell so clicking the checkbox is easier.
            p="0 !important"
            {...activeProps}
          >
            {isSelectable && (
              <SelectionCheckbox
                checked={isChecked}
                onCheckedChange={onCheckedChange}
                {...(verticalAlign === "top"
                  ? {
                      // When vertical aligning to the top, we should place the
                      // checkbox so that its center aligns with the center of the
                      // first line of text:
                      //
                      // cell-padding-top + (line-height / 2) - (checkbox-size / 2) - idk why but it looks better
                      pt: `calc(var(--chakra-sizes-5) + (1.5rem / 2) - (16px / 2) - 1px)`,
                      justifyContent: "flex-start",
                    }
                  : {})}
              />
            )}
          </BaseTableCell>
        )}
        {columnIds.map((columnId, idx) => {
          const column = config.columns[columnId];

          const value = column.extract(item, context);
          const rendered = column.render?.(value, item, context) ?? null;
          const disableRowInteraction =
            typeof column.disableRowInteraction === "function"
              ? column.disableRowInteraction(value, item, context)
              : (column.disableRowInteraction ?? false);
          const cellLink = cellRowLink(interaction, disableRowInteraction);

          return cellLink != null ? (
            <BaseTableCellLink
              key={columnId}
              to={cellLink}
              // Only the first cell in the row should be tabbable
              tabIndex={idx === 0 ? 0 : -1}
              alignItems={verticalAlign === "top" ? "flex-start" : "center"}
              // The link is a flex row, so its contents are placed, not text-aligned.
              justifyContent={column.align === "end" ? "flex-end" : column.align === "center" ? "center" : undefined}
              {...activeProps}
            >
              {rendered}
            </BaseTableCellLink>
          ) : (
            <BaseTableCell
              key={columnId}
              textAlign={column.align === "end" ? "end" : column.align === "center" ? "center" : undefined}
              {...(disableRowInteraction ? {} : pickProps)}
              {...activeProps}
            >
              {rendered}
            </BaseTableCell>
          );
        })}
      </>
    );

    return interaction.type === "navigate" ? <LinkProvider to={interaction.link}>{inner}</LinkProvider> : inner;
  },
);
